import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Tooltip, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CommentOutlinedIcon from '@material-ui/icons/CommentOutlined';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import NotificationsIcon from '@material-ui/icons/Notifications';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import { format, isPast } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';

import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import DialogViewWrapper from 'components/DialogViewWrapper';
import InformationSummary from 'components/InformationSummary';
import Loader from 'components/Loader';
import RichTextContent from 'components/RichTextContent/RichTextContent';
import actionInstanceStepApi from 'config/api/actionInstanceStep/actionInstanceStep';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { ActionLibraryInstance, ActionLibraryInstanceStepStatus } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import assets_messages from 'messages/assets_messages';
import PATHS from 'router/PATHS';

import ActionLibraryInstanceActions from './_components/ActionLibraryInstanceActions/ActionLibraryInstanceActions';
import ActionLibraryInstanceDetailsSteps from './_components/ActionLibraryInstanceDetailsSteps/ActionLibraryInstanceDetailsSteps';
import ActionLibraryInstanceProgress from './_components/ActionLibraryInstanceProgress/ActionLibraryInstanceProgress';
import CompleteInstanceDialog from './_components/CompleteInstanceDialog/CompleteInstanceDialog';
import InstanceCommentsDialog from './_components/InstanceCommentsDialog';
import InstanceStatusLogAccordion from './_components/InstanceStatusLogAccordion';
import ReopenInstanceDialog from './_components/ReopenInstanceDialog/ReopenInstanceDialog';
import useStyles from './ActionLibraryInstanceDetailPage.styles';

const ActionLibraryInstanceDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [instance, setInstance] = useState<ActionLibraryInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [instanceCommentsOpen, setInstanceCommentsOpen] = useState(false);
  const styles = useStyles();

  const instanceIdNum = id ? Number(id) : 0;
  const { data: commentsData } = useQuery(
    ['ACTION_LIBRARY_INSTANCE_COMMENTS', instanceIdNum],
    () => actionLibraryInstancesApi.getInstanceComments(instanceIdNum).then(res => res.data),
    { enabled: !!id },
  );
  const commentsCount = commentsData?.results?.length ?? 0;

  const loadInstance = React.useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const { data } = await actionLibraryInstancesApi.getSingleActionLibraryInstance(Number(id));
      setInstance(data);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const patchStepMutation = useMutation(
    ({ stepId, status }: { stepId: number; status: ActionLibraryInstanceStepStatus }) =>
      actionInstanceStepApi.patchStep(stepId, { status }),
    {
      onSuccess: async result => {
        queryClient.invalidateQueries('ACTION_LIBRARY_INSTANCES_GET_ALL');
        const updatedStep = result.data;
        setInstance(prev => {
          if (!prev?.steps) return prev;
          return {
            ...prev,
            steps: prev.steps.map(s =>
              s.id === updatedStep.id
                ? {
                    ...s,
                    status: updatedStep.status as ActionLibraryInstanceStepStatus,
                    statusDisplay: updatedStep.statusDisplay,
                    blockReason: updatedStep.blockReason ?? s.blockReason,
                    completionComment: updatedStep.completionComment ?? s.completionComment,
                  }
                : s,
            ),
          };
        });
        if (id) {
          const { data } = await actionLibraryInstancesApi.getSingleActionLibraryInstance(Number(id));
          setInstance(prev => (prev ? { ...prev, statusPercent: data.statusPercent } : null));
        }
      },
      onError: () => {
        enqueueSnackbar(t(action_library_messages.errors.instance_create_failed), { variant: 'error' });
      },
    },
  );

  useEffect(() => {
    if (id) loadInstance();
  }, [id, loadInstance]);

  const handleBack = () => {
    history.push(PATHS.ACTION_LIBRARY_INSTANCE);
  };

  const handleDelete = () => {
    history.push(PATHS.ACTION_LIBRARY_INSTANCE);
  };

  const handleStepStatusChange = useCallback(
    (stepId: number, status: ActionLibraryInstanceStepStatus) => {
      patchStepMutation.mutate({ stepId, status });
    },
    [patchStepMutation],
  );

  const canComplete = instance?.canComplete ?? false;

  const completeDisabledTooltip = useMemo(() => {
    if (!instance) return '';
    return instance.completeButtonTooltip || '';
  }, [instance]);

  const informationSummaryData = useMemo(() => {
    if (!instance) return [];

    const deadlineDate = instance.deadline ? new Date(instance.deadline) : null;
    const reminderDate = instance.reminder ? new Date(instance.reminder) : null;
    const isDeadlinePast = deadlineDate && isPast(deadlineDate) && instance.status !== 'completed';
    const isReminderPast = reminderDate && isPast(reminderDate) && instance.status !== 'completed';

    // Status with icon
    let StatusIconComponent;
    let statusIconClass;
    if (instance.status === 'completed') {
      StatusIconComponent = CheckCircleIcon;
      statusIconClass = styles.activeIcon;
    } else if (instance.status === 'in_progress') {
      StatusIconComponent = RemoveCircleOutlineIcon;
      statusIconClass = styles.criticalityMediumIcon;
    } else {
      StatusIconComponent = CancelIcon;
      statusIconClass = styles.inactiveIcon;
    }

    // Criticality with icon
    let CriticalityIconComponent;
    let criticalityIconClass;
    if (instance.criticality === 'low') {
      CriticalityIconComponent = InfoIcon;
      criticalityIconClass = styles.criticalityLowIcon;
    } else if (instance.criticality === 'medium') {
      CriticalityIconComponent = WarningIcon;
      criticalityIconClass = styles.criticalityMediumIcon;
    } else {
      CriticalityIconComponent = ErrorIcon;
      criticalityIconClass = styles.criticalityHighIcon;
    }

    const criticalityKey = instance.criticality.toUpperCase() as 'LOW' | 'MEDIUM' | 'HIGH';

    return [
      {
        label: action_library_messages.instance.detail.status,
        value: (
          <div className={styles.valueWithIcon}>
            <StatusIconComponent className={`${styles.statusIcon} ${statusIconClass}`} />
            <span>{instance.statusDisplay || instance.status}</span>
          </div>
        ),
      },
      {
        label: action_library_messages.instance.detail.criticality,
        value: (
          <div className={styles.valueWithIcon}>
            <CriticalityIconComponent className={`${styles.statusIcon} ${criticalityIconClass}`} />
            <span>{t(action_library_messages.criticality[criticalityKey])}</span>
          </div>
        ),
      },
      {
        label: action_library_messages.instance.detail.template_name,
        value: instance.templateName ?? '–',
      },
      {
        label: action_library_messages.instance.detail.template_source,
        value: instance.templateSourceDisplay ?? instance.templateSource ?? '–',
      },
      {
        label: action_library_messages.instance.detail.asset_name,
        value: instance.assetName || '-',
      },
      {
        label: action_library_messages.instance.detail.asset_type,
        value: instance.assetType || '-',
      },
      {
        label: action_library_messages.instance.detail.asset_importance,
        value: instance.assetImportance
          ? // @ts-ignore
            t(assets_messages.information_choices_assets_importance[instance.assetImportance]) || instance.assetImportance
          : '-',
      },
      {
        label: action_library_messages.instance.detail.owner,
        value: instance.ownerFullName || '-',
      },
      {
        label: action_library_messages.instance.detail.reported_to,
        value: instance.reportedTo ?? '-',
      },
      {
        label: action_library_messages.instance.detail.deadline,
        value: deadlineDate ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '5px' }}>
            <span style={{ color: isDeadlinePast ? '#f44336' : 'inherit', fontWeight: isDeadlinePast ? 'bold' : 'normal' }}>
              {format(deadlineDate, 'yyyy-MM-dd HH:mm')}
            </span>
          </div>
        ) : (
          '-'
        ),
      },
      {
        label: action_library_messages.instance.detail.reminder,
        value: reminderDate ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <NotificationsIcon style={{ fontSize: '1rem', color: isReminderPast ? '#ff9800' : 'rgba(0, 0, 0, 0.54)' }} />
            <span style={{ color: isReminderPast ? '#ff9800' : 'inherit' }}>{format(reminderDate, 'yyyy-MM-dd HH:mm')}</span>
          </div>
        ) : (
          '-'
        ),
      },
      {
        label: action_library_messages.instance.detail.validation_period,
        value:
          instance.validityPeriodMonths != null
            ? t(action_library_messages.instance.detail.validation_period_months, { count: instance.validityPeriodMonths })
            : '-',
      },
    ];
  }, [instance, t, styles]);

  if (loading) {
    return (
      <DialogViewWrapper contentSize='lg' onBack={handleBack} title={t(action_library_messages.instance.detail.page_title)}>
        <Loader inner />
      </DialogViewWrapper>
    );
  }

  if (error || !instance) {
    return (
      <DialogViewWrapper contentSize='lg' onBack={handleBack} title={t(action_library_messages.instance.detail.page_title)}>
        <div>{t(action_library_messages.error_state.title)}</div>
      </DialogViewWrapper>
    );
  }

  return (
    <DialogViewWrapper
      contentClassName={instance.status === 'completed' ? styles.completedContentBorder : undefined}
      contentSize='lg'
      onBack={handleBack}
      title={t(action_library_messages.instance.detail.page_title)}
    >
      <div className={styles.stickyMenuContainer}>
        <ActionLibraryInstanceActions
          canComplete={canComplete}
          canReopen={instance.canReopen === true}
          completeDisabledTooltip={completeDisabledTooltip}
          instance={instance}
          onCompleteClick={() => setCompleteDialogOpen(true)}
          onDelete={handleDelete}
          onInstanceUpdate={loadInstance}
          onReopenClick={() => setReopenDialogOpen(true)}
        />
        <CenteredGrid className={styles.stickyProgress} title='' width='lg' withoutPadding>
          <ActionLibraryInstanceProgress hideCompletedBadge status={instance.status} statusPercent={instance.statusPercent ?? 0} />
        </CenteredGrid>
      </div>
      <CenteredGrid
        iconLeft={null}
        rightAdornment={
          <Tooltip title={t(action_library_messages.instance_actions.instance_comments)}>
            <Button
              aria-label={t(action_library_messages.instance_actions.instance_comments)}
              onClick={() => setInstanceCommentsOpen(true)}
              size='small'
              startIcon={<CommentOutlinedIcon />}
            >
              {`${t(action_library_messages.instance_actions.instance_comments)} (${commentsCount})`}
            </Button>
          </Tooltip>
        }
        tight
        title={instance.title}
        width='lg'
        withoutPadding
      />
      <CenteredGrid gridGap={2} tight width='lg'>
        <Typography style={{ marginBottom: 8 }} variant='body2'>
          <RichTextContent content={instance.description || '-'} />
        </Typography>
      </CenteredGrid>

      <CenteredGrid gridGap={2} tight title='' width='lg'>
        <InformationSummary data={informationSummaryData} />
      </CenteredGrid>

      <CenteredGrid gridGap={2} tight title={t(action_library_messages.instance.detail.special_instruction)} width='lg'>
        <Typography variant='body2'>
          <RichTextContent content={instance.specialInstruction || '-'} />
        </Typography>
      </CenteredGrid>

      <InstanceStatusLogAccordion instanceId={instance.id} />

      {instance.status === 'completed' && instance.completionComment && (
        <CenteredGrid gridGap={2} tight title={t(action_library_messages.instance.detail.completion_comment)} width='lg'>
          <Typography variant='body2'>{instance.completionComment}</Typography>
        </CenteredGrid>
      )}

      {instance.steps && instance.steps.length > 0 && (
        <CenteredGrid gridGap={2} tight title={t(action_library_messages.instance.detail.steps)} width='lg'>
          <ActionLibraryInstanceDetailsSteps
            onStepStatusChange={instance.status === 'completed' ? undefined : handleStepStatusChange}
            readOnly={instance.status === 'completed'}
            steps={instance.steps}
          />
        </CenteredGrid>
      )}

      <CompleteInstanceDialog
        instance={instance}
        onClose={() => setCompleteDialogOpen(false)}
        onSuccess={loadInstance}
        open={completeDialogOpen}
      />
      <ReopenInstanceDialog
        instance={instance}
        onClose={() => setReopenDialogOpen(false)}
        onSuccess={loadInstance}
        open={reopenDialogOpen}
      />
      <InstanceCommentsDialog instanceId={instance.id} onClose={() => setInstanceCommentsOpen(false)} open={instanceCommentsOpen} />
    </DialogViewWrapper>
  );
};

export default ActionLibraryInstanceDetailPage;
